import type { ReactElement, ReactNode } from 'react'
import { Button, Checkbox, Tooltip, Typography } from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import LoadingBar from '@/components/LoadingBar'
import type { AdvisoryAiExportFormat, AdvisoryExport } from '@/features/advisory-ai/types'
import {
  AdvisoryExportStatus,
  buildExportFilename,
  exportEstimates,
  toExportStatus,
  type ExportOption,
} from './exportCardConfig'

const { Text } = Typography

function formatFileSize(bytes?: number | null): string {
  if (bytes == null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type ExportCardProps = {
  option: ExportOption
  exportItem?: AdvisoryExport
  requestedExportTypes: AdvisoryAiExportFormat[]
  completedExportTypes: Set<AdvisoryAiExportFormat>
  inProgressExportTypes: Set<AdvisoryAiExportFormat>
  hasExportInProgress: boolean
  orgToken: string
  meetingDateToken: string
  onGenerate: (exportType: AdvisoryAiExportFormat) => void
  /** Opening the file is the step's job — it also has to tell the server it happened. */
  onDownload: (exportItem: AdvisoryExport) => void
  selected: boolean
  selectionDisabled: boolean
  selectionDisabledReason?: string
  onToggleSelect: (exportType: AdvisoryAiExportFormat, next: boolean) => void
  bulkInProgress: boolean
  // When set, the output can't be generated yet (e.g. video needs an avatar + voice).
  // The notice explains what's missing and how to fix it.
  locked?: boolean
  lockedNotice?: ReactNode
}

export function ExportCard({
  option,
  exportItem,
  requestedExportTypes,
  completedExportTypes,
  inProgressExportTypes,
  hasExportInProgress,
  orgToken,
  meetingDateToken,
  onGenerate,
  onDownload,
  selected,
  selectionDisabled,
  selectionDisabledReason,
  onToggleSelect,
  bulkInProgress,
  locked = false,
  lockedNotice,
}: ExportCardProps): ReactElement {
  const isAlreadyGenerated = completedExportTypes.has(option.id)
  const isGenerating = requestedExportTypes.includes(option.id) || inProgressExportTypes.has(option.id)
  const fileSize = exportItem?.fileSize || null
  const pageCount = exportItem?.pageCount || null
  const exportStatus = toExportStatus(exportItem?.status)
  const isReady = exportStatus === AdvisoryExportStatus.COMPLETED_EXPORT && !!exportItem?.url
  const isExporting =
    exportStatus === AdvisoryExportStatus.EXPORTING ||
    exportStatus === AdvisoryExportStatus.PENDING_EXPORT ||
    isGenerating
  const isError = exportStatus === AdvisoryExportStatus.ERROR
  const isOutdated = isReady && !!exportItem?.isOutdated
  const expectedFileName = buildExportFilename({ exportType: option.id, orgToken, meetingDateToken })

  const checkbox = (
    <Checkbox
      checked={selected}
      disabled={selectionDisabled}
      onChange={(e) => onToggleSelect(option.id, e.target.checked)}
      aria-label={`Select ${option.title}`}
    />
  )

  return (
    <div
      className={`bg-white border rounded-lg p-6 flex items-start gap-4 transition-colors border-gray-200 ${
        isAlreadyGenerated || isGenerating ? 'bg-gray-50' : 'hover:border-gray-300'
      }`}
    >
      <div className="mt-1 flex-shrink-0">
        {selectionDisabled && selectionDisabledReason ? (
          <Tooltip title={selectionDisabledReason}>
            <span>{checkbox}</span>
          </Tooltip>
        ) : (
          checkbox
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div className="text-gray-600 mt-1">{option.icon}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{option.title}</h3>
              {locked ? (
                <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                  Setup required
                </span>
              ) : isGenerating ? (
                <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                  Generating
                </span>
              ) : isOutdated ? (
                <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                  Outdated
                </span>
              ) : isAlreadyGenerated ? (
                <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded">
                  Already generated
                </span>
              ) : null}
            </div>
            <p className="text-sm text-gray-600">{option.description}</p>
            {locked && lockedNotice ? <div className="mt-3">{lockedNotice}</div> : null}
            {(isExporting || isReady || isError || exportItem) && (
              <>
                <Text type="secondary" className="text-sm block mt-2">
                  {isReady
                    ? fileSize && pageCount
                      ? `${formatFileSize(fileSize)} - ${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`
                      : fileSize
                        ? formatFileSize(fileSize)
                        : 'Ready for download'
                    : isExporting
                      ? option.id === 'video'
                        ? `Your video is being generated! This usually takes ${exportEstimates.video.label}. Feel free to close the app — we'll have it ready when you return.`
                        : `This usually takes ${exportEstimates[option.id].label}. You can leave this page — it keeps running in the background.`
                      : isError
                        ? 'Export failed'
                        : 'Not generated yet'}
                </Text>
                {isExporting && (
                  <LoadingBar
                    indeterminate
                    label={`Generating ${option.title}`}
                    className="mt-2 max-w-md"
                  />
                )}
                <Text type="secondary" className="text-xs block mt-1">
                  File name: {expectedFileName}
                </Text>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 self-start flex-wrap justify-end">
            {!isReady && !isExporting && (
              <Button
                type="primary"
                loading={isGenerating}
                onClick={() => void onGenerate(option.id)}
                disabled={isAlreadyGenerated || isGenerating || hasExportInProgress || bulkInProgress || locked}
                className="feature-button-solid"
              >
                Generate
              </Button>
            )}
            {isReady && (
              <>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => exportItem?.url && onDownload(exportItem)}
                  disabled={bulkInProgress}
                >
                  Download
                </Button>
                {isOutdated && (
                  <Button
                    icon={<ReloadOutlined />}
                    loading={isGenerating}
                    onClick={() => void onGenerate(option.id)}
                    disabled={isGenerating || hasExportInProgress || bulkInProgress}
                  >
                    Regenerate
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
